import React from 'react';

import localStorage from '@client/core/utils/localStorage.js';
import { DevPanelContainer } from '@client/dev/components/DevPanelContainer.js';
import { ShortStatus } from '@client/dev/components/ShortStatus.js';
import { Tab } from '@client/dev/components/Tab.js';
import { DataTab } from '@client/dev/components/Tabs/DataTab.js';
import { ErrorTab } from '@client/dev/components/Tabs/ErrorTab.js';
import { ExperimentsTab } from '@client/dev/components/Tabs/ExperimentsTab.js';
import { FeatureFlagTab } from '@client/dev/components/Tabs/FeatureFlagTab.js';
import { PlacementsTab } from '@client/dev/components/Tabs/PlacementsTab.js';
import { RelevantTab } from '@client/dev/components/Tabs/RelevantTab.js';
import { SignificantEventsTab } from '@client/dev/components/Tabs/SignificantEventsTab.js';
import { ToolsTab } from '@client/dev/components/Tabs/ToolsTab.js';
import { TogglePanelButton } from '@client/dev/components/TogglePanelButton.js';

export function DevPanel() {
  const [isOpen, setIsOpen] = React.useState<boolean>(
    () => localStorage.getItem('ADVT_DEV_TOOL_isOpen') === 'true'
  );

  React.useEffect(() => {
    localStorage.setItem('ADVT_DEV_TOOL_isOpen', String(isOpen));
  }, [isOpen]);

  React.useEffect(() => {
    const savedIsOpen = localStorage.getItem('ADVT_DEV_TOOL_isOpen');
    if (savedIsOpen !== null) {
      setIsOpen(savedIsOpen === 'true');
    }
  }, []);

  const [SelectedTabComponent, setSelectedTabComponent] =
    DevPanel.TabManager.use();

  return (
    <DevPanelContainer isOpen={isOpen}>
      <TogglePanelButton isOpen={isOpen} setIsOpen={setIsOpen} />

      <div className="dev-panel__status s-bg-selected">
        <ShortStatus />

        <Tab.Group<DevPanel.TabManager.TabComponent>
          TabComponents={DevPanel.TabManager.TabComponent.values}
          SelectedTabComponent={SelectedTabComponent}
          onSelect={setSelectedTabComponent}
        />
      </div>

      <div className="overflow-auto">
        <SelectedTabComponent />
      </div>
    </DevPanelContainer>
  );
}

export namespace DevPanel.TabManager {
  export type TabComponent = (typeof TabComponent.values)[number];

  export namespace TabComponent {
    export const values = [
      PlacementsTab,
      DataTab,
      ErrorTab,
      SignificantEventsTab,
      FeatureFlagTab,
      ExperimentsTab,
      RelevantTab,
      ToolsTab
    ] as const satisfies ReadonlyArray<Tab>;
  }

  export function use() {
    const [SelectedTabComponent, _setSelectedTabComponent] = React.useState(
      () => Storage.readOrDefault(PlacementsTab)
    );

    const setSelectedTabComponent = React.useCallback(
      (TabComponent: DevPanel.TabManager.TabComponent) => {
        _setSelectedTabComponent(() => TabComponent);
      },
      [_setSelectedTabComponent]
    );

    Storage.use(SelectedTabComponent, setSelectedTabComponent);

    return [SelectedTabComponent, setSelectedTabComponent] as const;
  }

  export type FriendlyName = (typeof FriendlyName.values)[number];

  export namespace FriendlyName {
    export const values = [
      ...TabComponent.values.map(({ friendlyName }) => friendlyName)
    ] as const;

    export function is(friendlyName: string): friendlyName is FriendlyName {
      return TabComponent.values.some(
        (entry) => entry.friendlyName === friendlyName
      );
    }

    export function toTabComponent(
      friendlyName: FriendlyName
    ): TabComponent | null {
      return (
        TabComponent.values.find(
          (entry) => entry.friendlyName === friendlyName
        ) ?? null
      );
    }
  }

  export namespace Storage {
    export const key = 'ADVT_DEV_TOOL_selectedTab';

    export function use(
      SelectedTabComponent: TabComponent,
      select: (TabComponent: DevPanel.TabManager.TabComponent) => void
    ) {
      React.useEffect(() => {
        const SavedTabComponent = DevPanel.TabManager.Storage.readOrDefault();
        if (SavedTabComponent) select(SavedTabComponent);
      }, [select]);

      React.useEffect(() => {
        DevPanel.TabManager.Storage.write(SelectedTabComponent);
      }, [SelectedTabComponent]);
    }

    export function readOrDefault<T extends TabComponent | null>(
      defaultValue = null as T
    ): TabComponent | T {
      try {
        const friendlyName = localStorage.getItem(Storage.key);

        if (!friendlyName) return defaultValue;
        if (typeof friendlyName !== 'string') return defaultValue;
        if (!FriendlyName.is(friendlyName)) return defaultValue;

        return FriendlyName.toTabComponent(friendlyName) ?? defaultValue;
      } catch {
        return defaultValue;
      }
    }

    export function write(TabComponent: TabComponent) {
      localStorage.setItem(Storage.key, TabComponent.friendlyName);
    }
  }
}
